import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Activity,
  Clock,
  ShieldCheck,
  Pill,
  DollarSign,
  Package,
} from "lucide-react";
import { Link } from "react-router-dom";

const faqCategories = [
  {
    icon: <Activity className="w-6 h-6" />,
    title: "Product Benefits",
    questions: [
      {
        q: "What are the main benefits of ZivBio Testosterone Booster?",
        a: "ZivBio Testosterone Booster helps support natural testosterone levels, increase energy, enhance muscle growth, improve libido, and boost overall performance. Our formula is designed with premium natural ingredients backed by scientific research.",
      },
      {
        q: "How long does it take to see results?",
        a: "Most users report feeling increased energy and improved mood within 2-3 weeks of consistent use. For muscle growth and physical changes, optimal results are typically seen after 8-12 weeks of regular use combined with proper diet and exercise.",
      },
      {
        q: "Can I take this supplement with other supplements?",
        a: "ZivBio Testosterone Booster can be safely combined with most supplements. However, we recommend consulting with your healthcare provider before combining supplements, especially if you're taking any medications.",
      },
    ],
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Usage & Dosage",
    questions: [
      {
        q: "What is the recommended dosage?",
        a: "Take 2 capsules daily with water, preferably with your first meal. For enhanced results, you may take an additional capsule 30 minutes before workout, not exceeding 3 capsules per day.",
      },
      {
        q: "When is the best time to take it?",
        a: "For optimal results, take your daily dose in the morning with breakfast. If working out, you can take your pre-workout dose 30 minutes before exercise.",
      },
      {
        q: "Should I cycle this supplement?",
        a: "While cycling isn't required, some users prefer to cycle 12 weeks on and 4 weeks off. Consult with your healthcare provider for personalized advice.",
      },
    ],
  },
  {
    icon: <ShieldCheck className="w-6 h-6" />,
    title: "Safety & Side Effects",
    questions: [
      {
        q: "Are there any side effects?",
        a: "ZivBio Testosterone Booster is made with natural ingredients and is generally well-tolerated. Some users may experience mild effects like increased energy or improved sleep patterns as their body adjusts to the supplement.",
      },
      {
        q: "Is this product safe for long-term use?",
        a: "Yes, our product is formulated for long-term use with natural ingredients. However, we recommend consulting with your healthcare provider before starting any new supplement regimen.",
      },
    ],
  },
  {
    icon: <Pill className="w-6 h-6" />,
    title: "Ingredients",
    questions: [
      {
        q: "What are the key ingredients?",
        a: "Our formula contains Tribulus Terrestris (500mg), Ashwagandha (150mg), Tongkat Ali (200mg), Epimedium (160mg), DIM (100mg), and Zinc (20mg). Each ingredient is carefully selected for its specific benefits and synergistic effects.",
      },
      {
        q: "Are the ingredients natural?",
        a: "Yes, all ingredients in ZivBio Testosterone Booster are natural and sourced from high-quality suppliers. Our formula contains no artificial fillers or harmful additives.",
      },
    ],
  },
  {
    icon: <DollarSign className="w-6 h-6" />,
    title: "Orders & Shipping",
    questions: [
      {
        q: "Do you offer international shipping?",
        a: "Yes, we ship worldwide. Shipping times and costs vary by location. Free shipping is available for US orders over $50.",
      },
      {
        q: "What is your return policy?",
        a: "We offer a 30-day money-back guarantee. If you're not satisfied with your purchase, contact us for a full refund, no questions asked.",
      },
    ],
  },
  {
    icon: <Package className="w-6 h-6" />,
    title: "Storage & Handling",
    questions: [
      {
        q: "How should I store the product?",
        a: "Store in a cool, dry place away from direct sunlight. Keep the bottle tightly closed and out of reach of children.",
      },
      {
        q: "What is the shelf life?",
        a: "The product has a shelf life of 2 years when stored properly. Check the expiration date on the bottle for specific guidance.",
      },
    ],
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <motion.div initial={false} className="border-b border-gray-200">
      <button
        type="button"
        onClick={onToggle}
        className="w-full py-4 flex justify-between items-center text-left text-gray-800 hover:text-gray-600 transition-colors"
      >
        <span className="font-bold text-sm sm:text-base">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-gray-600 font-extrabold  text-sm sm:text-base">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQCategory = ({ category, icon, questions }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white backdrop-blur-sm rounded-lg p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow h-fit w-full"
    >
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className="p-2 bg-gradient-to-r from-primary-red to-primary-yellow rounded-lg text-white">
          {icon}
        </div>
        <h2 className="text-lg sm:text-xl font-bold text-[#be242c]">
          {category}
        </h2>
      </div>
      <div className="space-y-2">
        {questions.map((faq) => (
          <FAQItem
            key={faq.q}
            question={faq.q}
            answer={faq.a}
            isOpen={openIndex === faq.q}
            onToggle={() => setOpenIndex(openIndex === faq.q ? null : faq.q)}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default function Faqs() {
  return (
    <div className="relative  overflow-hidden min-h-screen">
      {/* Main background with diagonal split like homepage */}
      <div className="absolute inset-0 bg-[#e9b320]" />
      <div
        className="absolute top-1/4 right-0 w-full h-2/3 bg-[#a41e25]"
        style={{
          clipPath: "polygon(0 30%, 100% 53%, 100% 85%, 0 60%)",
        }}
      />
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 pt-36 max-w-7xl">
        <div className="flex flex-col items-center">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-16 max-w-4xl mx-auto"
          >
            <h1
              className="text-3xl tracking-wider sm:text-5xl lg:text-6xl  text-white mb-4 [text-shadow:2px_2px_0_#4d1312,4px_4px_0_#4d1312]"
              style={{
                fontFamily: "Headlined, sans-serif",
              }}
            >
              Frequently Asked Questions
            </h1>
            <p
              className="text-[#4d1312] max-w-2xl tracking-wider mx-auto text-xl sm:text-base lg:text-3xl [text-shadow:1px_0_0_#fcf5d4,2px_0_0_#fcf5d4,1px_1px_0_#fcf5d4,2px_2px_0_#fcf5d4]"
              style={{
                fontFamily: "Veneer, sans-serif",
              }}
            >
              Find answers to common questions about our product:
            </p>
          </motion.div>

          {/* FAQ Categories Grid */}
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 auto-rows-auto w-full max-w-6xl mx-auto">
            {faqCategories.map((category) => (
              <FAQCategory
                key={category.title}
                category={category.title}
                icon={category.icon}
                questions={category.questions}
              />
            ))}
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 sm:mt-16 text-center max-w-2xl mx-auto"
          >
            <h2
              className="text-xl sm:text-6xl   text-[#a41e25] mb-4 [text-shadow:1px_0_0_white,2px_0_0_white,1px_1px_0_white,2px_2px_0_white]"
              style={{
                fontFamily: "Headlined, sans-serif",
              }}
            >
              Still Have Questions?
            </h2>
            <p
              className="text-[#4d1312] max-w-3xl  mx-auto text-sm sm:text-base lg:text-3xl [text-shadow:1px_0_0_#fcf5d4,2px_0_0_#fcf5d4,1px_1px_0_#fcf5d4,2px_2px_0_#fcf5d4]"
              style={{
                fontFamily: "Veneer, sans-serif",
              }}
            >
              Our support team is here to help you with any questions or
              concerns.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-primary text-white px-6 sm:px-8 py-2.5 mt-10 sm:py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-sm sm:text-base"
              >
                Contact Support
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
